import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Checkbox, Chip, CircularProgress, Container, IconButton, ListItemText, MenuItem, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, InfoOutlined, Print, UnfoldLess, UnfoldMore, Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import MonthSelect from "../components/Inputs/MonthSelect";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import { useNavigation } from "../contexts/RouterContext";
import { filterFieldSx } from "../theme/filterStyles";
import { colors } from "../theme/designSystem";
import { maskName } from "../utils/nameMask";
import {
  CapacityEndingLink, CapacityHeatCell, CapacityTotals, CapacityWeekPoint, ProfessionalCapacityFilters,
  ProfessionalCapacityReport, ProfessionalCapacityRow, professionalCapacityService,
} from "../services/professionalCapacityService";

/**
 * Ocupação dos profissionais (RN-225): grade de disponibilidade (RN-117) × agenda, no mês escolhido e no
 * seguinte. A queda de um mês para o outro é o sinal de renovação: o acordeão mostra onde está o horário
 * (mapa de 30 min, do funcionamento −1h ao +1h, igual para todos) e quais vínculos paciente × profissional estão terminando.
 */

const SUNDAY = { value: 0, label: "Dom" };
const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: "Seg" }, { value: 2, label: "Ter" }, { value: 3, label: "Qua" },
  { value: 4, label: "Qui" }, { value: 5, label: "Sex" }, { value: 6, label: "Sáb" },
];
const WEEKDAY_NAME = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const OUTSIDE = "#fd7e14";
const HEAT_TEAL = { r: 3, g: 180, b: 198 };

type HeatView = "current" | "next" | "both";

const hoursBr = (v: number) => `${v.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 1 })}h`;
const pctBr = (v: number) => `${v.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
const dateBr = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};
const monthLabel = (iso: string) => {
  const nomes = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  const [y, m] = iso.split("-").map(Number);
  return `${nomes[m - 1]}/${String(y).slice(2)}`;
};
const minuteLabel = (minute: number) => `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};
const monthsAhead = (n: number) => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const heatColor = (percent: number) => {
  const t = Math.min(1, Math.max(0, percent / 100));
  const mix = (c: number) => Math.round(255 + (c - 255) * t);
  return `rgb(${mix(HEAT_TEAL.r)}, ${mix(HEAT_TEAL.g)}, ${mix(HEAT_TEAL.b)})`;
};

const occupancyTone = (percent: number) => (percent >= 85 ? colors.error : percent >= 60 ? colors.primary : "#b8860b");

const situationColor: Record<string, { bg: string; fg: string }> = {
  "Fim de série": { bg: colors.warningSoft, fg: "#856404" },
  "Série interrompida": { bg: "#fdecea", fg: "#8a1c1c" },
  Avulso: { bg: "#e9ecef", fg: "#495057" },
};

const FILTROS_INICIAIS: ProfessionalCapacityFilters = { month: currentMonth(), professionals: [], specialty: "", considerBlocks: true };

/** Barra + número da % ocupada. */
const PercentBar: React.FC<{ value: number }> = ({ value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
    <Box sx={{ flex: 1, height: 8, background: "#f1f3f5", borderRadius: 4, overflow: "hidden", minWidth: 40 }}>
      <Box sx={{ width: `${Math.min(100, value)}%`, height: "100%", background: occupancyTone(value), borderRadius: 4 }} />
    </Box>
    <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 44, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{pctBr(value)}</Typography>
  </Box>
);

/** Linha da % ocupada semana a semana; o trecho do mês seguinte é tracejado. */
const Sparkline: React.FC<{ weeks: CapacityWeekPoint[] }> = ({ weeks }) => {
  const pontos = weeks.filter((w) => w.totalHours > 0);
  if (pontos.length < 2) return <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>—</Typography>;
  const w = 120;
  const h = 30;
  const x = (i: number) => 3 + (i * (w - 6)) / (pontos.length - 1);
  const y = (p: number) => h - 3 - (Math.min(100, p) / 100) * (h - 6);
  const firstNext = pontos.findIndex((p) => p.nextMonth);
  const path = (from: number, to: number) => pontos.slice(from, to + 1).map((p, i) => `${x(from + i)},${y(p.occupiedPercent)}`).join(" ");
  const titulo = pontos.map((p) => `${dateBr(p.weekStart).slice(0, 5)}: ${pctBr(p.occupiedPercent)}`).join(" · ");
  return (
    <Tooltip title={`Semana a semana (início na segunda): ${titulo}`} arrow>
      <svg width={w} height={h} role="img" aria-label="Tendência da ocupação por semana">
        <line x1={0} x2={w} y1={y(50)} y2={y(50)} stroke="#e9ecef" strokeDasharray="2 2" />
        {firstNext === -1 ? (
          <polyline points={path(0, pontos.length - 1)} fill="none" stroke={colors.primary} strokeWidth={2} />
        ) : (
          <>
            {firstNext > 0 && <polyline points={path(0, firstNext)} fill="none" stroke={colors.primary} strokeWidth={2} />}
            <polyline points={path(Math.max(0, firstNext - 1), pontos.length - 1)} fill="none" stroke={colors.primary} strokeWidth={2} strokeDasharray="4 3" opacity={0.7} />
          </>
        )}
        {pontos.map((p, i) => <circle key={p.weekStart} cx={x(i)} cy={y(p.occupiedPercent)} r={2} fill={p.nextMonth ? "#fff" : colors.primary} stroke={colors.primary} />)}
      </svg>
    </Tooltip>
  );
};

/**
 * Mapa dia da semana × faixa de 30 min, do funcionamento −1h ao +1h (estendido por atendimento fora dele), com a mesma
 * escala para todos os profissionais. Grid de divs, não
 * <table>: o CSS das listas (`.c4u-table thead tr` vira flex) desalinhava o cabeçalho das horas dentro do acordeão.
 */
const CapacityHeatmap: React.FC<{ cells: CapacityHeatCell[]; first: number; last: number; slot: number; title: string }> = ({ cells, first, last, slot, title }) => {
  // O `title` só aparece com mouse; no toque a célula escolhida mostra o detalhe na linha abaixo do mapa
  const [picked, setPicked] = useState<{ key: string; text: string } | null>(null);
  const slots: number[] = [];
  for (let m = first; m < last; m += slot) slots.push(m);
  const byKey = new Map(cells.map((c) => [`${c.weekday}-${c.slotStart}`, c]));
  // Domingo só aparece como exceção: quando houve atendimento nele
  const days = cells.some((c) => c.weekday === 0) ? [...WEEKDAYS, SUNDAY] : WEEKDAYS;
  const grid = { display: "grid", gridTemplateColumns: `40px repeat(${slots.length}, minmax(22px, 1fr))`, gap: "2px", minWidth: 40 + slots.length * 24 };
  const label = { position: "sticky" as const, left: 0, zIndex: 1, background: "#f8f9fa", fontSize: 12, fontWeight: 600, color: colors.textSecondary, display: "flex", alignItems: "center" };

  const cell = (wd: number, m: number) => {
    const c = byKey.get(`${wd}-${m}`);
    const faixa = `${WEEKDAY_NAME[wd]} ${minuteLabel(m)}–${minuteLabel(m + slot)}`;
    const key = `${wd}-${m}`;
    const pick = (text: string) => ({
      title: text,
      onClick: () => setPicked((cur) => (cur?.key === key ? null : { key, text })),
    });
    const base = {
      height: 26, borderRadius: "3px", cursor: "pointer",
      outline: picked?.key === key ? `2px solid ${colors.textPrimary}` : "none", outlineOffset: "1px",
    };
    if (!c || (c.capacityMinutes === 0 && c.outsideMinutes === 0)) {
      return <Box key={m} {...pick(`${faixa}: fora da grade`)} sx={{ ...base, background: "#eceef0" }} />;
    }
    if (c.capacityMinutes === 0) {
      return <Box key={m} {...pick(`${faixa}: agendado FORA da grade (${Math.round(c.outsideMinutes)} min no período)`)} sx={{ ...base, background: OUTSIDE, opacity: 0.85 }} />;
    }
    const dica = [
      `${faixa}: ${pctBr(c.occupiedPercent)} ocupada`,
      `${Math.round(c.occupiedMinutes)} de ${Math.round(c.capacityMinutes)} min da grade no período`,
      c.outsideMinutes > 0 ? `+${Math.round(c.outsideMinutes)} min fora da grade` : "",
      c.endingSoon ? "vínculo terminando neste horário" : "",
    ].filter(Boolean).join(" · ");
    return (
      <Box key={m} {...pick(dica)}
        sx={{
          ...base,
          background: heatColor(c.occupiedPercent),
          border: c.occupiedPercent === 0 ? "1px solid #dee2e6" : "none",
          boxShadow: c.outsideMinutes > 0 ? `inset 0 -3px 0 ${OUTSIDE}` : "none",
          backgroundImage: c.endingSoon ? HATCH : "none",
        }} />
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      <Box className="capacity-scroll" sx={{ overflowX: "auto" }}>
        <Box sx={grid}>
          <Box sx={label} />
          {slots.map((m) => (
            <Box key={m} sx={{ fontSize: 10.5, fontWeight: 600, color: colors.textSecondary, whiteSpace: "nowrap", pb: 0.5, borderLeft: m % 60 === 0 ? "1px solid #ced4da" : "none", pl: "2px" }}>
              {m % 60 === 0 ? `${String(m / 60).padStart(2, "0")}h` : ""}
            </Box>
          ))}
          {days.map((wd) => (
            <React.Fragment key={wd.value}>
              <Box sx={label}>{wd.label}</Box>
              {slots.map((m) => cell(wd.value, m))}
            </React.Fragment>
          ))}
        </Box>
      </Box>
      <Typography aria-live="polite" sx={{ fontSize: 12.5, mt: 0.75, minHeight: 20, color: picked ? colors.textPrimary : colors.textSecondary }}>
        {picked ? picked.text : "Toque ou clique num horário para ver o detalhe."}
      </Typography>
    </Box>
  );
};

// No modo cartão (global.css, max-width 768px) o título do cartão é o profissional, ao lado do chevron.
// O `&&&&` + !important vence a especificidade das regras globais do cartão (`.c4u-table--serial .c4u-table__row > *`).
const CARD = "@media (max-width: 768px)";

const HATCH = "repeating-linear-gradient(45deg, rgba(255,255,255,0.65) 0 3px, transparent 3px 7px)";

/** Legenda do mapa; cada item explica a cor ao passar o mouse (ou tocar, no celular). */
const LEGEND: { sx: object; text: string; hint: string }[] = [
  { sx: { background: "#fff", border: "1px solid #dee2e6" }, text: "Livre na grade", hint: "Horário que está na grade do profissional e ficou livre no mês: é horário para vender. A borda cinza é só o contorno do quadrado." },
  { sx: { background: heatColor(50) }, text: "Ocupada em parte das semanas", hint: "Ocupada em algumas semanas do mês, livre nas outras. Quanto mais forte o azul, em mais semanas esteve ocupada." },
  { sx: { background: heatColor(100) }, text: "Ocupada em todas as semanas", hint: "A meia hora esteve ocupada em todas as semanas do mês." },
  { sx: { background: heatColor(100), backgroundImage: HATCH }, text: "Vínculo terminando", hint: "Um paciente cuja série termina neste horário, sem nada agendado depois. Se ninguém renovar, o horário abre." },
  { sx: { background: heatColor(100), boxShadow: `inset 0 -3px 0 ${OUTSIDE}` }, text: "Parte fora da grade", hint: "Traço laranja na base: a sessão começou dentro da grade e passou para fora dela (ex.: 08:30–09:30 numa grade que termina às 09:00)." },
  { sx: { background: OUTSIDE, opacity: 0.85 }, text: "Agendado fora da grade", hint: "Houve atendimento num horário que a grade diz que o profissional não atende. Não entra na %; quase sempre é grade desatualizada." },
  { sx: { background: "#eceef0" }, text: "Fora da grade", hint: "Horário em que o profissional não declarou disponibilidade (ou a clínica está fechada) e não houve atendimento. Não conta em nada." },
];

const Legend: React.FC = () => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1.5 }}>
    {LEGEND.map((l) => (
      <Tooltip key={l.text} title={l.hint} arrow enterTouchDelay={0}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, cursor: "help" }} tabIndex={0}>
          <Box sx={{ width: 18, height: 14, borderRadius: "3px", ...l.sx }} />
          <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, borderBottom: "1px dotted #adb5bd" }}>{l.text}</Typography>
        </Box>
      </Tooltip>
    ))}
  </Box>
);

const EndingLinks: React.FC<{ links: CapacityEndingLink[]; showNames: boolean }> = ({ links, showNames }) => {
  if (links.length === 0) {
    return <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>Nenhum vínculo terminando nesta janela.</Typography>;
  }
  return (
    <Box className="c4u-table">
      <Box className="c4u-table__header">
        <Box className="admin-plans-header-cell" sx={{ flex: "1 1 180px" }}>Paciente</Box>
        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 150px" }}>Horário</Box>
        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 100px" }}>Última sessão</Box>
        <Box className="admin-plans-header-cell" sx={{ flex: "0 0 150px" }}>Situação</Box>
        <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "1 1 160px" }}>Serviço</Box>
      </Box>
      {links.map((l) => {
        const cor = situationColor[l.situation] ?? situationColor.Avulso;
        return (
          <Box key={`${l.patientId}-${l.lastDate}`} className="c4u-table__row">
            <Box className="admin-plans-cell" data-label="Paciente" sx={{ flex: "1 1 180px", minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{showNames ? l.patientName : maskName(l.patientName)}</Typography>
            </Box>
            <Box className="admin-plans-cell" data-label="Horário" sx={{ flex: "0 0 150px", fontSize: 13 }}>
              {WEEKDAY_NAME[l.weekday]} {l.startTime}–{l.endTime}
            </Box>
            <Box className="admin-plans-cell" data-label="Última sessão" sx={{ flex: "0 0 100px", fontSize: 13 }}>{dateBr(l.lastDate)}</Box>
            <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: "0 0 150px", display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
              <Chip size="small" label={l.situation} sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, bgcolor: cor.bg, color: cor.fg }} />
              {l.occurrence && <Typography sx={{ fontSize: 11, color: colors.textSecondary }}>{l.occurrence}</Typography>}
            </Box>
            <Box className="admin-plans-cell c4u-col--lg" data-label="Serviço" sx={{ flex: "1 1 160px", minWidth: 0, fontSize: 12.5, overflowWrap: "anywhere" }}>{l.serviceName ?? "—"}</Box>
          </Box>
        );
      })}
    </Box>
  );
};

const ReportProfessionalCapacity: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();

  const [filtros, setFiltros] = useState<ProfessionalCapacityFilters>(FILTROS_INICIAIS);
  const [data, setData] = useState<ProfessionalCapacityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const [heatView, setHeatView] = useState<HeatView>("current");
  const [showNames, setShowNames] = useState(false);
  const [comoLer, setComoLer] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      setData(await professionalCapacityService.get(filtros));
    } catch {
      setErro("Não foi possível carregar o relatório.");
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  const linhas = useMemo(() => data?.professionals ?? [], [data]);
  const opcoes = useMemo(
    () => (data?.professionalOptions ?? []).filter((o) => !filtros.specialty || o.specialty === filtros.specialty),
    [data, filtros.specialty],
  );
  const temFiltro = JSON.stringify(filtros) !== JSON.stringify(FILTROS_INICIAIS);
  const todosAbertos = linhas.length > 0 && linhas.every((l) => abertos.has(l.professionalId));
  const terminando = useMemo(() => linhas.flatMap((l) => l.endingLinks), [linhas]);
  const fimDeSerie = terminando.filter((l) => l.situation === "Fim de série").length;
  const interrompidas = terminando.filter((l) => l.situation === "Série interrompida").length;

  const alternar = (id: string) => setAbertos((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const alternarTodos = () => setAbertos(todosAbertos ? new Set() : new Set(linhas.map((l) => l.professionalId)));

  const mudaProfissionais = (ids: string[]) => setFiltros((f) => ({ ...f, professionals: ids }));
  const selecionarTodos = () => mudaProfissionais(filtros.professionals.length === opcoes.length ? [] : opcoes.map((o) => o.id));

  const mesAtual = data ? monthLabel(data.currentFrom) : "";
  const mesSeguinte = data ? monthLabel(data.nextFrom) : "";

  const subCard = (seguinte: CapacityTotals, campo: keyof CapacityTotals, pct = false) =>
    `${mesSeguinte}: ${pct ? pctBr(seguinte[campo]) : hoursBr(seguinte[campo])}`;

  const grupos = useMemo(() => {
    const mapa = new Map<string, ProfessionalCapacityRow[]>();
    linhas.forEach((l) => mapa.set(l.specialty, [...(mapa.get(l.specialty) ?? []), l]));
    return Array.from(mapa.entries());
  }, [linhas]);

  const heatTitle = (quando: "current" | "next") =>
    quando === "current" ? `Mês escolhido (${mesAtual})` : `Mês seguinte (${mesSeguinte}) — agenda ainda em formação`;

  return (
    <Box className="c4u-print-page" sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Ocupação dos profissionais</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Horas da grade de disponibilidade contra o agendado, no mês escolhido e no seguinte, por profissional e especialidade.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} className="no-print">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <MonthSelect label="Competência" value={filtros.month} onChange={(v) => setFiltros((f) => ({ ...f, month: v }))} maxMonth={monthsAhead(6)} sx={filterFieldSx} />
                <TextField
                  select size="small" label="Especialidade" value={filtros.specialty}
                  onChange={(e) => setFiltros((f) => ({ ...f, specialty: e.target.value, professionals: [] }))}
                  fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {(data?.specialtyOptions ?? []).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                <TextField
                  select size="small" label="Profissionais" value={filtros.professionals}
                  onChange={(e) => {
                    const v = e.target.value as unknown as string[] | string;
                    mudaProfissionais((typeof v === "string" ? v.split(",") : v).filter((x) => x !== "all"));
                  }}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (sel) => {
                      const ids = sel as string[];
                      if (ids.length === 0) return "Todos";
                      if (ids.length > 2) return `${ids.length} selecionados`;
                      return opcoes.filter((o) => ids.includes(o.id)).map((o) => o.name).join(", ");
                    },
                  }}
                  fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}
                >
                  <MenuItem
                    value="all"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); selecionarTodos(); }}
                    sx={{ backgroundColor: "#f8f9fa !important", fontWeight: 600, fontSize: "0.85rem", borderBottom: "1px solid #e9ecef" }}
                  >
                    <Checkbox
                      checked={opcoes.length > 0 && filtros.professionals.length === opcoes.length}
                      indeterminate={filtros.professionals.length > 0 && filtros.professionals.length < opcoes.length}
                      sx={{ padding: "4px 8px 4px 4px", "& .MuiSvgIcon-root": { fontSize: "1.2rem" } }}
                    />
                    <ListItemText primary={`Selecionar Todos (${opcoes.length})`} primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }} />
                  </MenuItem>
                  {opcoes.map((o) => (
                    <MenuItem key={o.id} value={o.id} sx={{ fontSize: "0.85rem" }}>
                      <Checkbox checked={filtros.professionals.includes(o.id)} sx={{ padding: "4px 8px 4px 4px", "& .MuiSvgIcon-root": { fontSize: "1.2rem" } }} />
                      <ListItemText primary={o.name} secondary={o.specialty} primaryTypographyProps={{ fontSize: "0.85rem" }} secondaryTypographyProps={{ fontSize: "0.72rem" }} />
                    </MenuItem>
                  ))}
                </TextField>
                {/* Item 210 (decisão do Hilton, 01/10): almoço e reunião bloqueados não são horário de atendimento */}
                <TextField
                  select size="small" label="Bloqueios de agenda" value={filtros.considerBlocks ? "on" : "off"}
                  onChange={(e) => setFiltros((f) => ({ ...f, considerBlocks: e.target.value === "on" }))}
                  fullWidth sx={filterFieldSx} InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="on">Descontar da disponibilidade</MenuItem>
                  <MenuItem value="off">Ignorar (grade cadastrada)</MenuItem>
                </TextField>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={() => setFiltros(FILTROS_INICIAIS)} disabled={!temFiltro || loading} />
                <Button
                  variant="contained" size="small" onClick={() => setShowNames((v) => !v)}
                  startIcon={showNames ? <VisibilityOff /> : <Visibility />}
                  sx={{ textTransform: "none", backgroundColor: "#6c757d", "&:hover": { backgroundColor: "#5a6268" }, height: 40 }}
                >
                  {showNames ? "Ocultar nomes" : "Exibir nomes"}
                </Button>
                <Tooltip title="Imprimir" arrow>
                  <span>
                    <IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => window.print()} disabled={loading || linhas.length === 0} aria-label="Imprimir"><Print /></IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            <Box className="c4u-info-box no-print">
              <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
              <Box>
                <Typography>
                  H/Totais é a grade de disponibilidade de cada profissional dentro do funcionamento da clínica, sem feriados.
                  {filtros.considerBlocks
                    ? ' Os bloqueios de agenda (almoço, reunião) saem dela, salvo onde houve atendimento por cima.'
                    : ' Os bloqueios de agenda estão sendo ignorados: vale a grade cadastrada.'}
                  H/Ocupadas é o agendado dentro dela (cancelado libera; falta ocupa, porque o horário ficou reservado).
                  A conta é em minutos: sessão de 30 min ocupa meia hora. O mês seguinte está sempre mais vazio, porque as
                  séries ainda não foram criadas ou renovadas — a queda entre os dois meses é o sinal de renovação.
                </Typography>
                <Typography component="button" type="button" onClick={() => setComoLer((v) => !v)}
                  sx={{ mt: 0.5, p: 0, border: 0, background: "none", color: colors.primary, fontWeight: 600, fontSize: "inherit", cursor: "pointer", textDecoration: "underline" }}>
                  {comoLer ? "Ocultar como ler o mapa" : "Como ler o mapa de horários"}
                </Typography>
                {comoLer && (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ mb: 1 }}>
                      Cada linha é um dia da semana e cada quadrado, meia hora, de 1h antes da abertura da clínica a 1h depois do fechamento — ou mais, quando houve atendimento fora desse horário (o domingo só aparece nesse caso). A cor resume todas as semanas
                      do mês naquele horário (as quatro segundas, as quatro terças…). "Grade" é a disponibilidade que o
                      profissional tem cadastrada em Usuários › Disponibilidade.
                    </Typography>
                    {LEGEND.map((l) => (
                      <Box key={l.text} sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 0.5 }}>
                        <Box sx={{ width: 18, height: 14, borderRadius: "3px", flexShrink: 0, mt: "3px", ...l.sx }} />
                        <Typography><strong>{l.text}:</strong> {l.hint}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            ) : erro || !data ? (
              <Typography sx={{ textAlign: "center", color: colors.error, py: 4 }}>{erro ?? "Sem dados."}</Typography>
            ) : (
              <>
                <Box sx={summaryCardsRowSx} className="c4u-print-block">
                  <ReportSummaryCard label={`Horas totais (${mesAtual})`} value={hoursBr(data.current.totalHours)} sub={subCard(data.next, "totalHours")} />
                  <ReportSummaryCard label="Horas ocupadas" value={hoursBr(data.current.occupiedHours)} sub={subCard(data.next, "occupiedHours")} />
                  <ReportSummaryCard label="Horas disponíveis" value={hoursBr(data.current.availableHours)} sub={subCard(data.next, "availableHours")} />
                  <ReportSummaryCard label="% ocupada" value={pctBr(data.current.occupiedPercent)} sub={subCard(data.next, "occupiedPercent", true)} />
                  <ReportSummaryCard
                    label="Vínculos terminando"
                    value={String(terminando.length)}
                    sub={`${fimDeSerie} fim de série · ${interrompidas} interrompidas`}
                    subColor={fimDeSerie > 0 ? "#856404" : undefined}
                    note={`última sessão de ${data.endingPastDays} dias atrás a ${data.endingAheadDays} dias à frente`}
                  />
                </Box>
                {data.current.outsideGridHours > 0 && (
                  <Typography sx={{ fontSize: 12.5, color: "#8a4a0b", mb: 1.5 }}>
                    + {hoursBr(data.current.outsideGridHours)} agendadas fora da grade em {mesAtual} — grade desatualizada ou profissional sem grade cadastrada.
                  </Typography>
                )}

                <Box className="no-print" sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
                  <ResultCount shown={linhas.length} />
                  <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <ToggleButtonGroup
                      size="small" exclusive value={heatView} onChange={(_, v) => v && setHeatView(v)}
                      sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: 12.5, py: 0.5, px: 1.25 }, "& .Mui-selected": { color: `${colors.primary} !important`, backgroundColor: "rgba(3,180,198,0.08) !important" } }}
                    >
                      <ToggleButton value="current">{mesAtual}</ToggleButton>
                      <ToggleButton value="next">{mesSeguinte}</ToggleButton>
                      <ToggleButton value="both">Os dois</ToggleButton>
                    </ToggleButtonGroup>
                    <Tooltip title={todosAbertos ? "Fechar todos" : "Abrir todos"} arrow>
                      <span>
                        <IconButton size="small" onClick={alternarTodos} disabled={linhas.length === 0} sx={{ color: colors.primary }} aria-label={todosAbertos ? "Fechar todos" : "Abrir todos"}>
                          {todosAbertos ? <UnfoldLess fontSize="small" /> : <UnfoldMore fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                {linhas.length === 0 ? (
                  <Typography sx={{ px: 2, py: 3, fontSize: 13, color: "#adb5bd", textAlign: "center" }}>
                    Nenhum profissional com grade de disponibilidade ou agenda nos filtros escolhidos.
                  </Typography>
                ) : (
                  <Box className="c4u-table c4u-table--serial">
                    <Box className="c4u-table__header">
                      <Box className="admin-plans-header-cell" sx={{ flex: "0 0 48px", justifyContent: "center" }} />
                      <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 1 150px" }}>Especialidade</Box>
                      <Box className="admin-plans-header-cell" sx={{ flex: "1 1 170px" }}>Nome</Box>
                      <Box className="admin-plans-header-cell" sx={{ flex: "0 0 78px", justifyContent: "flex-end" }}>H/Totais</Box>
                      <Box className="admin-plans-header-cell" sx={{ flex: "0 0 86px", justifyContent: "flex-end" }}>H/Ocupadas</Box>
                      <Box className="admin-plans-header-cell" sx={{ flex: "0 0 96px", justifyContent: "flex-end" }}>H/Disponíveis</Box>
                      <Box className="admin-plans-header-cell" sx={{ flex: "1 1 150px" }}>% Ocupada</Box>
                      <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 104px", justifyContent: "flex-end" }}>{`% ${mesSeguinte}`}</Box>
                      <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: "0 0 130px" }}>Tendência</Box>
                    </Box>

                    {grupos.map(([especialidade, profs]) => {
                      const sub = data.bySpecialty.find((s) => s.specialty === especialidade);
                      return (
                        <React.Fragment key={especialidade}>
                          <div className="c4u-group-row">
                            <strong>{especialidade}</strong>
                            {sub && (
                              <span className="c4u-group-row__detail">
                                {sub.professionals} {sub.professionals === 1 ? "profissional" : "profissionais"} · {hoursBr(sub.current.occupiedHours)} de {hoursBr(sub.current.totalHours)} ·{" "}
                                {pctBr(sub.current.occupiedPercent)} em {mesAtual} → {pctBr(sub.next.occupiedPercent)} em {mesSeguinte}
                              </span>
                            )}
                          </div>
                          {profs.map((p) => {
                            const aberto = abertos.has(p.professionalId);
                            const queda = p.current.occupiedPercent - p.next.occupiedPercent;
                            return (
                              <React.Fragment key={p.professionalId}>
                                <Box className="c4u-table__row">
                                  <Box className="admin-plans-cell" data-label="Ver" sx={{ flex: "0 0 48px", display: "flex", justifyContent: "center", [CARD]: { "&&&&": { order: "-2 !important" } } }}>
                                    <Tooltip title={aberto ? "Fechar" : "Abrir mapa de horários"} arrow>
                                      <IconButton size="small" onClick={() => alternar(p.professionalId)} sx={{ color: colors.primary }} aria-expanded={aberto} aria-label={aberto ? "Fechar" : "Abrir mapa de horários"}>
                                        {aberto ? <ExpandLess /> : <ExpandMore />}
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                  <Box className="admin-plans-cell c4u-col--md" data-label="Especialidade" sx={{ flex: "0 1 150px", fontSize: 12.5, color: colors.textSecondary, minWidth: 0, [CARD]: { "&&&&": { display: "none !important" } } }}>{p.specialty}</Box>
                                  <Box className="admin-plans-cell" data-label="Nome" sx={{ flex: "1 1 170px", minWidth: 0, [CARD]: { "&&&&": { order: "-1 !important", flex: "1 1 0 !important", display: "block !important" }, "&&&&::before": { display: "none !important" } } }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, overflowWrap: "anywhere" }}>{p.name}</Typography>
                                    {!p.hasGrid && <Typography sx={{ fontSize: 11, color: "#8a4a0b" }}>sem grade cadastrada</Typography>}
                                    {p.endingLinks.length > 0 && (
                                      <Typography sx={{ fontSize: 11, color: "#856404" }}>{p.endingLinks.length} {p.endingLinks.length === 1 ? "vínculo terminando" : "vínculos terminando"}</Typography>
                                    )}
                                  </Box>
                                  <Box className="admin-plans-cell" data-label="H/Totais" sx={{ flex: "0 0 78px", justifyContent: "flex-end", fontVariantNumeric: "tabular-nums" }}>{hoursBr(p.current.totalHours)}</Box>
                                  <Box className="admin-plans-cell" data-label="H/Ocupadas" sx={{ flex: "0 0 86px", justifyContent: "flex-end", fontVariantNumeric: "tabular-nums", flexDirection: "column", alignItems: "flex-end" }}>
                                    <span>{hoursBr(p.current.occupiedHours)}</span>
                                    {p.current.outsideGridHours > 0 && (
                                      <Tooltip title="Agendado fora da grade — não entra na %" arrow>
                                        <Typography sx={{ fontSize: 10.5, color: "#8a4a0b" }}>+{hoursBr(p.current.outsideGridHours)} fora</Typography>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Box className="admin-plans-cell" data-label="H/Disponíveis" sx={{ flex: "0 0 96px", justifyContent: "flex-end", fontVariantNumeric: "tabular-nums" }}>{hoursBr(p.current.availableHours)}</Box>
                                  <Box className="admin-plans-cell" data-label="% Ocupada" sx={{ flex: "1 1 150px", minWidth: 0 }}><PercentBar value={p.current.occupiedPercent} /></Box>
                                  <Box className="admin-plans-cell c4u-col--md" data-label={`% ${mesSeguinte}`} sx={{ flex: "0 0 104px", justifyContent: "flex-end", flexDirection: "column", alignItems: "flex-end" }}>
                                    <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 13 }}>{pctBr(p.next.occupiedPercent)}</span>
                                    {p.current.totalHours > 0 && Math.abs(queda) >= 1 && (
                                      <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: queda >= 20 ? colors.error : queda > 0 ? "#b8860b" : "#155724" }}>
                                        {queda > 0 ? "▼" : "▲"} {Math.abs(queda).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} p.p.
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box className="admin-plans-cell c4u-col--lg" data-label="Tendência" sx={{ flex: "0 0 130px" }}><Sparkline weeks={p.weeks} /></Box>
                                </Box>

                                {aberto && (
                                  <Box className="c4u-table__expand" sx={{ px: { xs: 1.5, md: 3 }, py: 2, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e2e8f0", mb: { xs: 1.5, md: 0 } }}>
                                    {(heatView === "current" || heatView === "both") && (
                                      <CapacityHeatmap cells={p.heatCurrent} first={data.firstMinute} last={data.lastMinute} slot={data.slotMinutes} title={heatTitle("current")} />
                                    )}
                                    {(heatView === "next" || heatView === "both") && (
                                      <CapacityHeatmap cells={p.heatNext} first={data.firstMinute} last={data.lastMinute} slot={data.slotMinutes} title={heatTitle("next")} />
                                    )}
                                    <Legend />
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>Vínculos terminando</Typography>
                                    <EndingLinks links={p.endingLinks} showNames={showNames} />
                                  </Box>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
      <style>{`
        @media print {
          .capacity-scroll { overflow: visible !important; }
        }
      `}</style>
    </Box>
  );
};

export default ReportProfessionalCapacity;
